import { STORAGE_KEYS } from "../../utils/constants/storageKeys";
import { setAuthSession } from "./authStorage";
import { isElectron } from "./index";

const PENDING_AUTH_ERROR_KEY = "orderly:pending-auth-error";

const dispatchAuthError = (message) => {
  sessionStorage.setItem(PENDING_AUTH_ERROR_KEY, message);
  window.dispatchEvent(new CustomEvent("orderly:auth-error", { detail: { message } }));
};

export const consumePendingDesktopAuthError = () => {
  const message = sessionStorage.getItem(PENDING_AUTH_ERROR_KEY);
  sessionStorage.removeItem(PENDING_AUTH_ERROR_KEY);
  return message;
};

export const pathForDesktopDeepLink = (payload) => {
  if (payload.kind === "navigate") return payload.route;
  if (payload.kind !== "printer-register") return null;

  const search = new URLSearchParams({
    ip: payload.host,
    port: String(payload.port)
  });
  return `/printer-register?${search.toString()}`;
};

export const hasMatchingOAuthState = (receivedState, expectedState) =>
  typeof expectedState === "string" &&
  expectedState.length > 0 &&
  receivedState === expectedState;

const clearOAuthState = () => {
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
  sessionStorage.removeItem("facebookOAuthState");
  sessionStorage.removeItem("fb_oauth_state");
};

const handleDeepLink = async (payload) => {
  const destination = pathForDesktopDeepLink(payload);
  if (destination) {
    window.location.assign(destination);
    return;
  }

  if (payload.kind !== "auth-callback" && payload.kind !== "auth-error") {
    throw new TypeError("Unsupported desktop deep-link payload");
  }

  const expectedState =
    sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE) ||
    sessionStorage.getItem("facebookOAuthState") ||
    sessionStorage.getItem("fb_oauth_state");

  if (!hasMatchingOAuthState(payload.state, expectedState)) {
    dispatchAuthError("The sign-in response could not be verified. Please try again.");
    return;
  }

  // Consume a valid state before doing any asynchronous work so the same callback
  // cannot be replayed while session persistence is in progress.
  clearOAuthState();

  if (payload.kind === "auth-error") {
    dispatchAuthError(payload.message);
    return;
  }

  await setAuthSession(payload.token, payload.role, true);
  window.history.replaceState({}, "", "/billing");
  window.location.reload();
};

const safelyHandleDeepLink = async (payload) => {
  try {
    await handleDeepLink(payload);
  } catch (error) {
    console.error("Unable to process a desktop deep link", error);
    dispatchAuthError("The requested desktop action could not be completed.");
  }
};

export const initializeDesktopIntegration = async () => {
  if (!isElectron()) return () => undefined;

  const unsubscribe = window.electronAPI.app.onDeepLink((payload) => {
    void safelyHandleDeepLink(payload);
  });

  try {
    const [appInfo, pending] = await Promise.all([
      window.electronAPI.app.getInfo(),
      window.electronAPI.app.consumePendingDeepLinks()
    ]);
    document.documentElement.dataset.electronPlatform = appInfo.platform;
    document.documentElement.dataset.electron = "true";
    for (const payload of pending) await safelyHandleDeepLink(payload);
    return unsubscribe;
  } catch (error) {
    unsubscribe();
    throw error;
  }
};
